import {
  Box,
  // Chip,
  Paper,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  Typography,
  TableSortLabel,
  TableContainer,
  TablePagination,
  CircularProgress,
  Chip,
} from '@mui/material';
import { IColumnDefinition, TASK_PRIORITY } from '@common/types';
import {
  ColumnNames,
  ITaskTableEntry,
  ITaskTableFilters,
  useTaskTable,
} from './hooks';
import { formatDateShort } from '@util/date.ts';
import { theme } from '@store';
import { SearchOff } from '@mui/icons-material';
import { t } from 'i18next';
import { TruncatedTextWithConditionalTooltip } from '@components';
import dayjs from 'dayjs';
import { getDaysPast } from '@util/getDaysPast.ts';
import { getPriorityColor } from '@util/getPriorityColor.ts';
import { statusMapper } from 'util/statusMapper.ts';

interface ITaskTableProps {
  rows: ITaskTableEntry[];
  columns: IColumnDefinition[];
  filters: ITaskTableFilters;
  setFilters: (filters: ITaskTableFilters) => void;
  totalCount: number;
  onSelectTask: (taskId: string) => void;
  loading: boolean;
  selectedTaskId: string;
  tabPanelInstrumentHeight: number;
  patient_id: string;
}

export const TaskTable = ({
  rows,
  filters,
  columns,
  setFilters,
  totalCount,
  onSelectTask,
  loading,
  selectedTaskId,
  tabPanelInstrumentHeight,
}: ITaskTableProps) => {
  const { sort_by, sort_order } = filters;
  const { onPageSizeChange, handleChangeOrder, onPageIndexChange } =
    useTaskTable({ filters, setFilters });

  const today = new Date();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 0,
          boxShadow: 'none',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          maxHeight: `calc(100vh - ${tabPanelInstrumentHeight + 155}px)`,
        }}
      >
        <Table stickyHeader size='small'>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  paddingY: '1rem',
                  bgcolor: theme => theme.palette.headerBackground.main,
                },
              }}
            >
              {columns.map((col, index) => (
                <TableCell
                  key={`${col.id}-${index}`}
                  align={col.align || 'left'}
                >
                  {col.sortable ? (
                    <TableSortLabel
                      active={true}
                      direction={
                        col.id === sort_by
                          ? (sort_order?.toLowerCase() as 'asc' | 'desc')
                          : 'asc'
                      }
                      onClick={() => handleChangeOrder(col.id as ColumnNames)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length > 0 && !loading && (
              <>
                {rows.map(row => (
                  <TableRow
                    onClick={() => onSelectTask(row.id)}
                    key={row.id}
                    sx={{
                      cursor: 'pointer',

                      '& .MuiTableCell-body': {
                        paddingY: '1rem',
                      },
                      '&:hover, &:nth-of-type(even):hover': {
                        backgroundColor: theme.palette.hoverTableRowBg.main,
                      },
                      ...(selectedTaskId === row.id && {
                        bgcolor: theme.palette.selectedTableRowBg.main,
                        '&:nth-of-type(even)': {
                          bgcolor: theme.palette.selectedTableRowBg.main,
                        },
                      }),
                    }}
                  >
                    <TableCell sortDirection='desc' component='th' scope='row'>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <TruncatedTextWithConditionalTooltip
                            text={row.task}
                            sx={{ fontSize: 14 }}
                          />
                          <TruncatedTextWithConditionalTooltip
                            variant='body2'
                            color={theme.palette.semiBlack.main}
                            text={row.program || 'N/A'}
                            sx={{ fontSize: 12 }}
                          />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align='center'>
                      <Box
                        sx={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        {row.status === 'Completed' ||
                        row.status ===
                          'Completed (Without requirements completion)' ||
                        row.status === 'Archived' ? (
                          '-'
                        ) : (
                          <Chip
                            sx={{
                              backgroundColor: getPriorityColor(row.priority),
                              color: theme => theme.palette.common.white,
                              height: '24px',
                              width: '103px',
                            }}
                            label={row.priority || 'Priority unknown'}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align='left'>
                      <TruncatedTextWithConditionalTooltip
                        color={
                          (today > new Date(row.due_date) ||
                            row.status === TASK_PRIORITY.OVERDUE) &&
                          row.status !== 'Completed' &&
                          row.status !== 'Archived'
                            ? 'red'
                            : 'unset'
                        }
                        variant='body2'
                        sx={{ fontSize: 14 }}
                        text={formatDateShort(row.due_date)}
                      />
                    </TableCell>
                    <TableCell align='center'>
                      <TruncatedTextWithConditionalTooltip
                        sx={{
                          fontSize: 14,
                        }}
                        color={row.status === 'Completed' ? 'success' : 'unset'}
                        text={
                          row.canvas_status ||
                          statusMapper({
                            status: row.status,
                            canvas_status: row.canvas_status,
                          })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          minWidth: '100px',
                        }}
                      >
                        <Typography variant='body1' sx={{ fontSize: '14px' }}>
                          {formatDateShort(row.created_date)}
                        </Typography>
                        <Typography
                          variant='body2'
                          sx={{ fontSize: '12px', color: 'text.disabled' }}
                        >
                          {row.status !== 'Completed' &&
                          row.status !==
                            'Completed (Without requirements completion)'
                            ? getDaysPast(row.created_date)
                            : ''}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          minWidth: '100px',
                        }}
                      >
                        <Typography variant='body1' sx={{ fontSize: '14px' }}>
                          {formatDateShort(row.last_updated_at)}
                        </Typography>
                        <Typography
                          variant='body2'
                          sx={{ fontSize: '12px', color: 'text.disabled' }}
                        >
                          {dayjs(row.last_updated_at).format('hh:mm A')}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {rows.length === 0 && !loading && (
        <Box
          sx={{
            bgcolor: theme.palette.common.white,
            display: 'grid',
            placeItems: 'center',
            width: '100%',
            height: `calc(100vh - ${tabPanelInstrumentHeight + 155}px)`,
            borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
            borderRight: '1px solid rgba(0, 0, 0, 0.12)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            <SearchOff sx={{ width: 46, height: 46, mb: 3 }} color='primary' />
            <Typography variant='h6'>
              {t('general.table.noResults.title')}
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              {t('general.table.noResults.subTitle')}
            </Typography>
          </Box>
        </Box>
      )}
      {loading && (
        <Box
          sx={{
            bgcolor: theme.palette.common.white,
            display: 'grid',
            placeItems: 'center',
            width: '100%',
            height: `calc(100vh - ${tabPanelInstrumentHeight + 155}px)`,
            borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
            borderRight: '1px solid rgba(0, 0, 0, 0.12)',
          }}
        >
          <CircularProgress size='64px' />
        </Box>
      )}
      <TablePagination
        sx={{
          borderBottom: 0,
          paddingY: '0.75rem',
          bgcolor: 'white',
          border: theme => `1px solid ${theme.palette.grey[300]}`,
        }}
        component='div'
        count={totalCount}
        page={filters.page! - 1}
        rowsPerPage={filters.limit!}
        onPageChange={onPageIndexChange}
        onRowsPerPageChange={onPageSizeChange}
      />
    </Box>
  );
};
